/**
 * Operational Insights - the third round of strategic rollups: financial
 * ratios/solvency, transport/hostel/canteen utilization, scholarship
 * effectiveness, utility consumption trends, parent engagement, vendor
 * performance, health/discipline trends, classroom-space utilization, and
 * a data-quality/record-completeness check.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import operationalInsightsService from '../services/operationalInsightsService';
import schoolsService from '../services/schoolsService';
import { CircleNotch } from '@phosphor-icons/react';
import './OperationalInsightsPage.css';

const TABS = [
  { key: 'financial', label: 'Financial Ratios' },
  { key: 'transport', label: 'Transport' },
  { key: 'hostel', label: 'Hostel' },
  { key: 'canteen', label: 'Canteen' },
  { key: 'scholarship', label: 'Scholarship Effectiveness' },
  { key: 'utility', label: 'Utility Trends' },
  { key: 'parent-engagement', label: 'Parent Engagement' },
  { key: 'vendor', label: 'Vendor Performance' },
  { key: 'health', label: 'Health Trends' },
  { key: 'discipline', label: 'Discipline Trends' },
  { key: 'space', label: 'Space Utilization' },
  { key: 'data-quality', label: 'Data Quality' },
];

function OperationalInsightsPage() {
  const [tab, setTab] = useState('financial');
  const [terms, setTerms] = useState([]);

  useEffect(() => {
    schoolsService.listAllTerms().then((d) => setTerms(d?.items || [])).catch(() => {});
  }, []);

  return (
    <Layout>
      <div className="page operational-insights-page">
        <div className="page-header">
          <div>
            <h1>Operational Insights</h1>
            <p>Financial health, facility/fleet utilization, scholarship outcomes, engagement, vendors, and data quality</p>
          </div>
        </div>

        <div className="oip-tabs">
          {TABS.map((t) => (
            <button key={t.key} type="button" className={tab === t.key ? 'btn btn-primary' : 'btn btn-secondary'} onClick={() => setTab(t.key)}>
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'financial' && <FinancialTab />}
        {tab === 'transport' && <TransportTab />}
        {tab === 'hostel' && <HostelTab />}
        {tab === 'canteen' && <CanteenTab />}
        {tab === 'scholarship' && <ScholarshipTab terms={terms} />}
        {tab === 'utility' && <UtilityTab />}
        {tab === 'parent-engagement' && <ParentEngagementTab />}
        {tab === 'vendor' && <VendorTab />}
        {tab === 'health' && <HealthTab />}
        {tab === 'discipline' && <DisciplineTab />}
        {tab === 'space' && <SpaceTab terms={terms} />}
        {tab === 'data-quality' && <DataQualityTab />}
      </div>
    </Layout>
  );
}

function KeyValueTable({ data, keyLabel = 'Key', valueLabel = 'Value' }) {
  const entries = Object.entries(data || {});
  if (entries.length === 0) return <p className="empty">No data</p>;
  return (
    <div className="oip-table">
      <table>
        <thead><tr><th>{keyLabel}</th><th>{valueLabel}</th></tr></thead>
        <tbody>{entries.map(([k, v]) => <tr key={k}><td>{k}</td><td>{v}</td></tr>)}</tbody>
      </table>
    </div>
  );
}

function FinancialTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    operationalInsightsService.getFinancialRatios().then(setData).catch((e) => toast.error('Failed to load financial ratios: ' + e.message)).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="loading"><CircleNotch size={28} weight="bold" /> Loading...</div>;

  return (
    <div className="oip-panel">
      <div className="oip-card">
        <div className="oip-stat-row">
          <div className="oip-stat"><span className="oip-stat-value">{data.current_ratio ?? '—'}</span><span className="oip-stat-label">Current Ratio</span></div>
          <div className="oip-stat"><span className="oip-stat-value">{data.debt_to_equity ?? '—'}</span><span className="oip-stat-label">Debt to Equity</span></div>
          <div className="oip-stat"><span className="oip-stat-value">{data.days_cash_on_hand ?? '—'}</span><span className="oip-stat-label">Days Cash on Hand</span></div>
          <div className="oip-stat"><span className="oip-stat-value">GHS {data.cash_balance?.toLocaleString()}</span><span className="oip-stat-label">Cash Balance</span></div>
        </div>
        <h4>Operating Margin Trend</h4>
        <div className="oip-table"><table>
          <thead><tr><th>Month</th><th>Revenue</th><th>Expenses</th><th>Operating Margin</th></tr></thead>
          <tbody>{data.operating_margin_trend.map((t) => (
            <tr key={t.month}><td>{t.month}</td><td>GHS {t.revenue.toLocaleString()}</td><td>GHS {t.expenses.toLocaleString()}</td><td>{t.operating_margin_pct ?? '—'}%</td></tr>
          ))}</tbody>
        </table></div>
      </div>
    </div>
  );
}

function TransportTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    operationalInsightsService.getTransportUtilization().then(setData).catch((e) => toast.error('Failed to load transport data: ' + e.message)).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="loading"><CircleNotch size={28} weight="bold" /> Loading...</div>;

  return (
    <div className="oip-panel">
      <div className="oip-card">
        <div className="oip-stat-row">
          <div className="oip-stat"><span className="oip-stat-value">{data.total_vehicles}</span><span className="oip-stat-label">Total Vehicles</span></div>
          <div className="oip-stat"><span className="oip-stat-value">GHS {data.total_maintenance_cost.toLocaleString()}</span><span className="oip-stat-label">Total Maintenance Cost</span></div>
        </div>
        {data.routes.length === 0 ? <p className="empty">No routes set up</p> : (
          <div className="oip-table"><table>
            <thead><tr><th>Route</th><th>Students</th><th>Vehicle Capacity</th><th>Utilization</th><th>Fee Revenue</th></tr></thead>
            <tbody>{data.routes.map((r) => (
              <tr key={r.route_id}><td>{r.route_name}</td><td>{r.student_count}</td><td>{r.vehicle_capacity ?? '—'}</td><td>{r.utilization_pct ?? '—'}%</td><td>GHS {r.fee_revenue_collected.toLocaleString()}</td></tr>
            ))}</tbody>
          </table></div>
        )}
      </div>
    </div>
  );
}

function HostelTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    operationalInsightsService.getHostelOccupancy().then(setData).catch((e) => toast.error('Failed to load hostel data: ' + e.message)).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="loading"><CircleNotch size={28} weight="bold" /> Loading...</div>;

  return (
    <div className="oip-panel">
      <div className="oip-card">
        <div className="oip-stat-row">
          <div className="oip-stat"><span className="oip-stat-value">{data.overall_occupancy_rate}%</span><span className="oip-stat-label">Overall Occupancy</span></div>
          <div className="oip-stat"><span className="oip-stat-value">{data.total_occupied}/{data.total_capacity}</span><span className="oip-stat-label">Occupied/Capacity</span></div>
          <div className="oip-stat"><span className="oip-stat-value">{data.revenue_per_occupied_bed != null ? `GHS ${data.revenue_per_occupied_bed.toLocaleString()}` : '—'}</span><span className="oip-stat-label">Revenue / Occupied Bed</span></div>
        </div>
        {data.hostels.length === 0 ? <p className="empty">No hostels set up</p> : (
          <div className="oip-table"><table>
            <thead><tr><th>Hostel</th><th>Type</th><th>Rooms</th><th>Capacity</th><th>Occupied</th><th>Rate</th></tr></thead>
            <tbody>{data.hostels.map((h) => (
              <tr key={h.hostel_id}><td>{h.hostel_name}</td><td>{h.hostel_type}</td><td>{h.total_rooms}</td><td>{h.capacity}</td><td>{h.occupied}</td><td>{h.occupancy_rate}%</td></tr>
            ))}</tbody>
          </table></div>
        )}
      </div>
    </div>
  );
}

function CanteenTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    operationalInsightsService.getCanteenFinancials().then(setData).catch((e) => toast.error('Failed to load canteen data: ' + e.message)).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="loading"><CircleNotch size={28} weight="bold" /> Loading...</div>;

  return (
    <div className="oip-panel">
      <div className="oip-card">
        <div className="oip-stat-row"><div className="oip-stat"><span className="oip-stat-value">GHS {data.total_revenue.toLocaleString()}</span><span className="oip-stat-label">Total Revenue</span></div></div>
        <h4>Revenue by Month</h4>
        <KeyValueTable data={data.revenue_by_month} keyLabel="Month" valueLabel="Revenue (GHS)" />
        <h4>Wallet Top-Ups by Month</h4>
        <KeyValueTable data={data.wallet_topups_by_month} keyLabel="Month" valueLabel="Top-Ups (GHS)" />
        <h4>Popular Items</h4>
        {data.popular_items.length === 0 ? <p className="empty">No data</p> : (
          <div className="oip-table"><table>
            <thead><tr><th>Item</th><th>Quantity</th><th>Revenue</th></tr></thead>
            <tbody>{data.popular_items.map((i) => <tr key={i.item_name}><td>{i.item_name}</td><td>{i.quantity}</td><td>GHS {i.revenue.toLocaleString()}</td></tr>)}</tbody>
          </table></div>
        )}
      </div>
    </div>
  );
}

function ScholarshipTab({ terms }) {
  const [termId, setTermId] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!termId && terms.length) setTermId(terms.find((t) => t.is_current)?.id || terms[0].id);
  }, [terms, termId]);

  const load = useCallback(async () => {
    if (!termId) return;
    setLoading(true);
    try { setData(await operationalInsightsService.getScholarshipEffectiveness(termId)); }
    catch (error) { toast.error('Failed to load scholarship data: ' + error.message); }
    finally { setLoading(false); }
  }, [termId]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="oip-panel">
      <div className="form-group oip-inline-filter">
        <label>Academic Term</label>
        <select value={termId} onChange={(e) => setTermId(e.target.value)}>
          {terms.map((t) => <option key={t.id} value={t.id}>{t.academic_year} — {t.term}</option>)}
        </select>
      </div>
      {loading ? (
        <div className="loading"><CircleNotch size={28} weight="bold" /> Loading...</div>
      ) : data && (
        <div className="oip-table"><table>
          <thead><tr><th></th><th>Count</th><th>Avg GPA</th><th>Retention Rate</th></tr></thead>
          <tbody>
            <tr><td>Scholarship Recipients</td><td>{data.scholarship_recipients.count}</td><td>{data.scholarship_recipients.avg_gpa ?? '—'}</td><td>{data.scholarship_recipients.retention_rate ?? '—'}%</td></tr>
            <tr><td>Non-Recipients</td><td>{data.non_recipients.count}</td><td>{data.non_recipients.avg_gpa ?? '—'}</td><td>{data.non_recipients.retention_rate ?? '—'}%</td></tr>
          </tbody>
        </table></div>
      )}
    </div>
  );
}

function UtilityTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    operationalInsightsService.getUtilityTrends().then(setData).catch((e) => toast.error('Failed to load utility trends: ' + e.message)).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="loading"><CircleNotch size={28} weight="bold" /> Loading...</div>;

  const types = Object.keys(data.consumption_by_type_and_month || {});

  return (
    <div className="oip-panel">
      <div className="oip-card">
        <div className="oip-stat-row"><div className="oip-stat"><span className="oip-stat-value">{data.meters_tracked}</span><span className="oip-stat-label">Meters Tracked</span></div></div>
        {types.length === 0 ? <p className="empty">No utility readings recorded yet</p> : types.map((t) => (
          <div key={t}>
            <h4>{t} — Consumption by Month</h4>
            <KeyValueTable data={data.consumption_by_type_and_month[t]} keyLabel="Month" valueLabel="Consumption" />
          </div>
        ))}
        <h4>Total Cost by Type</h4>
        <KeyValueTable data={data.total_cost_by_type} keyLabel="Type" valueLabel="Cost (GHS)" />
      </div>
    </div>
  );
}

function ParentEngagementTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    operationalInsightsService.getParentEngagement().then(setData).catch((e) => toast.error('Failed to load parent engagement: ' + e.message)).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="loading"><CircleNotch size={28} weight="bold" /> Loading...</div>;

  return (
    <div className="oip-panel">
      <div className="oip-card">
        <div className="oip-stat-row">
          <div className="oip-stat"><span className="oip-stat-value">{data.total_parents_with_portal_access}</span><span className="oip-stat-label">Parents w/ Portal Access</span></div>
          <div className="oip-stat"><span className="oip-stat-value">{data.portal_login_rate_30d ?? '—'}%</span><span className="oip-stat-label">Login Rate (30d)</span></div>
          <div className="oip-stat"><span className="oip-stat-value">{data.message_read_rate ?? '—'}%</span><span className="oip-stat-label">Message Read Rate</span></div>
          <div className="oip-stat"><span className="oip-stat-value">{data.ptm_booking_confirmation_rate ?? '—'}%</span><span className="oip-stat-label">PTM Confirmation Rate</span></div>
        </div>
      </div>
    </div>
  );
}

function VendorTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    operationalInsightsService.getVendorPerformance().then(setData).catch((e) => toast.error('Failed to load vendor data: ' + e.message)).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="loading"><CircleNotch size={28} weight="bold" /> Loading...</div>;

  return (
    <div className="oip-panel">
      {data.suppliers.length === 0 ? <div className="empty-state"><p>No purchase orders recorded yet</p></div> : (
        <div className="oip-table"><table>
          <thead><tr><th>Supplier</th><th>Orders</th><th>On-Time Delivery Rate</th></tr></thead>
          <tbody>{data.suppliers.map((s) => <tr key={s.supplier_id}><td>{s.supplier_name}</td><td>{s.order_count}</td><td>{s.on_time_delivery_rate ?? '—'}%</td></tr>)}</tbody>
        </table></div>
      )}
    </div>
  );
}

function HealthTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    operationalInsightsService.getHealthTrends().then(setData).catch((e) => toast.error('Failed to load health trends: ' + e.message)).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="loading"><CircleNotch size={28} weight="bold" /> Loading...</div>;

  return (
    <div className="oip-panel">
      <div className="oip-card">
        <div className="oip-stat-row">
          <div className="oip-stat"><span className="oip-stat-value">{data.total_visits}</span><span className="oip-stat-label">Total Clinic Visits</span></div>
          <div className="oip-stat"><span className="oip-stat-value">{data.immunization_compliance_rate ?? '—'}%</span><span className="oip-stat-label">Immunization Compliance</span></div>
        </div>
        <h4>Visits by Month</h4>
        <KeyValueTable data={data.visits_by_month} keyLabel="Month" valueLabel="Visits" />
      </div>
    </div>
  );
}

function DisciplineTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    operationalInsightsService.getDisciplineTrends().then(setData).catch((e) => toast.error('Failed to load discipline trends: ' + e.message)).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="loading"><CircleNotch size={28} weight="bold" /> Loading...</div>;

  return (
    <div className="oip-panel">
      <div className="oip-card">
        <div className="oip-stat-row"><div className="oip-stat"><span className="oip-stat-value">{data.total_incidents}</span><span className="oip-stat-label">Total Incidents</span></div></div>
        <h4>By Month</h4>
        <KeyValueTable data={data.by_month} keyLabel="Month" />
        <h4>By Category</h4>
        <KeyValueTable data={data.by_category} keyLabel="Category" />
        <h4>By Severity</h4>
        <KeyValueTable data={data.by_severity} keyLabel="Severity" />
      </div>
    </div>
  );
}

function SpaceTab({ terms }) {
  const [termId, setTermId] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!termId && terms.length) setTermId(terms.find((t) => t.is_current)?.id || terms[0].id);
  }, [terms, termId]);

  const load = useCallback(async () => {
    if (!termId) return;
    setLoading(true);
    try { setData(await operationalInsightsService.getSpaceUtilization(termId)); }
    catch (error) { toast.error('Failed to load space utilization: ' + error.message); }
    finally { setLoading(false); }
  }, [termId]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="oip-panel">
      <div className="form-group oip-inline-filter">
        <label>Academic Term</label>
        <select value={termId} onChange={(e) => setTermId(e.target.value)}>
          {terms.map((t) => <option key={t.id} value={t.id}>{t.academic_year} — {t.term}</option>)}
        </select>
      </div>
      {loading ? (
        <div className="loading"><CircleNotch size={28} weight="bold" /> Loading...</div>
      ) : data?.classes.length === 0 ? (
        <div className="empty-state"><p>No timetable entries for this term</p></div>
      ) : data && (
        <div className="oip-table"><table>
          <thead><tr><th>Class</th><th>Periods Booked</th><th>Max Periods/Week</th><th>Utilization</th></tr></thead>
          <tbody>{data.classes.map((c) => (
            <tr key={c.class_id}><td>{c.class_name}</td><td>{c.periods_booked}</td><td>{c.max_periods_per_week}</td><td>{c.utilization_pct ?? '—'}%</td></tr>
          ))}</tbody>
        </table></div>
      )}
    </div>
  );
}

function DataQualityTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    operationalInsightsService.getDataQuality().then(setData).catch((e) => toast.error('Failed to load data quality: ' + e.message)).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="loading"><CircleNotch size={28} weight="bold" /> Loading...</div>;

  return (
    <div className="oip-panel oip-two-col">
      <div className="oip-card">
        <h3>Students ({data.students.total})</h3>
        <div className="oip-stat-row">
          <div className="oip-stat"><span className="oip-stat-value">{data.students.missing_photo_pct ?? '—'}%</span><span className="oip-stat-label">Missing Photo</span></div>
          <div className="oip-stat"><span className="oip-stat-value">{data.students.missing_address_pct ?? '—'}%</span><span className="oip-stat-label">Missing Address</span></div>
          <div className="oip-stat"><span className="oip-stat-value">{data.students.missing_class_assignment_pct ?? '—'}%</span><span className="oip-stat-label">Unassigned to a Class</span></div>
        </div>
      </div>
      <div className="oip-card">
        <h3>Staff ({data.staff.total})</h3>
        <div className="oip-stat-row">
          <div className="oip-stat"><span className="oip-stat-value">{data.staff.missing_photo_pct ?? '—'}%</span><span className="oip-stat-label">Missing Photo</span></div>
          <div className="oip-stat"><span className="oip-stat-value">{data.staff.missing_department_pct ?? '—'}%</span><span className="oip-stat-label">Missing Department</span></div>
          <div className="oip-stat"><span className="oip-stat-value">{data.staff.missing_qualification_pct ?? '—'}%</span><span className="oip-stat-label">Missing Qualification</span></div>
        </div>
      </div>
    </div>
  );
}

export default OperationalInsightsPage;
